// Invoices tab — replaces both the legacy Documents tab AND the standalone
// Reconcile tab. Every row is one invoice and carries its own match
// status + inline reconciliation actions, so the user never has to leave
// this view to match a doc to a bank transaction.
//
// Columns:
//   File         — parent artifact filename (click to open)
//   Merchant     — vendor / merchant
//   Reference    — invoice number / RO # / check #
//   Description  — payee / line memo
//   Amount       — line amount (currency-formatted)
//   Invoice date — when issued
//   Due date     — when payment is due
//   Paid date    — when payment cleared (from the matched txn if any)
//   Match        — match status chip + transaction + Approve / Unlink /
//                  Find-match action buttons
//   Status       — extraction status (Extracted / Needs review / etc.)
//
// Toolbar:
//   - Search box (debounced)
//   - Merchant scope (Autocomplete)
//   - Bank scope (Autocomplete) — only constrains the match side
//   - Match filter: All / Unmatched / Need review / Matched / Mismatched
//   - Extraction status filter
//   - From / to date inputs
//   - "Run auto-match" button (replaces the Reconcile tab's button)

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Stack, Typography, Button, IconButton, Tooltip, Chip,
    Table, TableHead, TableBody, TableRow, TableCell, TextField,
    Alert, CircularProgress, MenuItem, FormControl, InputLabel, Select,
    LinearProgress, Autocomplete, Drawer, Divider, Paper, Checkbox,
    Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup, Radio,
    FormControlLabel,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SearchIcon from '@mui/icons-material/Search';
import StoreIcon from '@mui/icons-material/Store';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import GridOnIcon from '@mui/icons-material/GridOn';
import {
    rpaInvoiceList, rpaMerchantList, rpaFileUrl,
    rpaReconciliationAutoMatch, rpaReconciliationSuggest,
    rpaReconciliationManualLink, rpaReconciliationUnlink,
    rpaReconciliationReviewMatch, rpaListStripeAccounts,
    rpaReconciliationUnmatchedTxns,
} from './api';
import {
    KpiCard, KpiGrid, SectionPaper, TableShell, EmptyState, Toolbar,
    RPA_COLORS, formatDateShort, formatMoney,
} from './rpaTheme';


const STATUS_TONE = {
    extracted:      { label: 'Extracted',     color: 'success',
                      icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
    corrected:      { label: 'Corrected',     color: 'info',
                      icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
    needs_review:   { label: 'Needs review',  color: 'warning',
                      icon: <WarningAmberIcon sx={{ fontSize: 14 }} /> },
    extract_failed: { label: 'Extract failed',color: 'error',
                      icon: <ErrorOutlineIcon sx={{ fontSize: 14 }} /> },
    superseded:     { label: 'Superseded',    color: 'default',
                      icon: <HelpOutlineIcon sx={{ fontSize: 14 }} /> },
};


export default function InvoiceList() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [merchants, setMerchants] = useState([]);
    const [accounts, setAccounts] = useState([]);

    // Filters
    const [statusFilter, setStatusFilter] = useState('');
    const [matchFilter, setMatchFilter] = useState('');
    const [merchantFilter, setMerchantFilter] = useState(null);
    const [accountFilter, setAccountFilter] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    // Pagination
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);

    // Reconciliation auto-match summary modal-style state.
    const [autoMatchRunning, setAutoMatchRunning] = useState(false);
    const [autoMatchSummary, setAutoMatchSummary] = useState(null);

    // Manual-link drawer — shows suggested bank transactions for the
    // currently-picked unmatched invoice row.
    const [linkRow, setLinkRow] = useState(null);
    const [linkSuggestions, setLinkSuggestions] = useState([]);
    const [linkLoading, setLinkLoading] = useState(false);

    // Period Report export dialog. Filters are seeded from the
    // current Invoices tab scope (bank, merchant, date range) so the
    // user usually just picks a format and hits Export — no
    // re-entering filters in two places.
    const [exportOpen, setExportOpen] = useState(false);
    const [exportFormat, setExportFormat] = useState('xlsx');
    const [exportBank, setExportBank] = useState(null);
    const [exportMerchant, setExportMerchant] = useState(null);
    const [exportFromDate, setExportFromDate] = useState('');
    const [exportToDate, setExportToDate] = useState('');
    const [exportRunning, setExportRunning] = useState(false);

    const openExportDialog = () => {
        // Pre-fill from the active tab filters when the dialog opens
        // — most users export the slice they're currently looking at.
        setExportFormat('xlsx');
        setExportBank(accountFilter);
        setExportMerchant(merchantFilter);
        setExportFromDate(fromDate);
        setExportToDate(toDate);
        setExportOpen(true);
    };
    const closeExportDialog = () => setExportOpen(false);

    const runExport = async () => {
        setExportRunning(true);
        setError(null);
        try {
            // Build the query string by hand — we need the response
            // as a Blob (file download), not as JSON, so we use the
            // fetch API path instead of the axios JSON helpers.
            const qs = new URLSearchParams();
            qs.set('format', exportFormat);
            if (exportFromDate) qs.set('from_date', exportFromDate);
            if (exportToDate)   qs.set('to_date', exportToDate);
            if (exportBank != null)     qs.set('stripe_account_id', String(exportBank));
            if (exportMerchant != null) qs.set('merchant_id', String(exportMerchant));

            // Reuse axiosInstance so the JWT + team-id headers travel.
            const axios = (await import('../../../utils/axiosInstance')).default;
            const res = await axios.get(
                `/api/finance/rpa/reports/period-report?${qs.toString()}`,
                { responseType: 'blob' },
            );

            // Some 4xx / 5xx replies come back as JSON-shaped blobs;
            // try to read those as text so the user sees the message
            // instead of a binary garbage download.
            const ctype = res.headers?.['content-type'] || '';
            if (ctype.includes('application/json')) {
                const text = await res.data.text();
                try {
                    const parsed = JSON.parse(text);
                    throw new Error(parsed.message || 'Export failed');
                } catch (e2) {
                    throw new Error(text || 'Export failed');
                }
            }

            // Filename from Content-Disposition (the backend sets it
            // to "period-report-<from>_to_<to>.<ext>").
            const cd = res.headers?.['content-disposition'] || '';
            const m = /filename="?([^";]+)"?/i.exec(cd);
            const filename = m ? m[1]
                : `period-report.${exportFormat}`;

            // Trigger the download via an anchor click — works in
            // every modern browser without a hidden iframe.
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            closeExportDialog();
        } catch (e) {
            setError(e?.response?.data?.message
                     || e?.message
                     || 'Export failed');
        } finally {
            setExportRunning(false);
        }
    };

    // Multi-select for the N-to-1 manual-link flow ("link these 3
    // invoices to one wire"). Keyed by row.id (synthetic line-item id
    // from the backend), not document_id — so the user can clearly
    // see WHICH lines they picked even within one parent doc. The
    // backend's manual_link_many endpoint takes document_ids, so we
    // dedupe the picked rows down to unique parent docs before sending.
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const [bulkLinkOpen, setBulkLinkOpen] = useState(false);
    const [bulkCandidates, setBulkCandidates] = useState([]);
    const [bulkLoading, setBulkLoading] = useState(false);

    const toggleRow = (rowId) => {
        setSelectedRowIds((prev) =>
            prev.includes(rowId)
                ? prev.filter((x) => x !== rowId)
                : [...prev, rowId],
        );
    };
    const clearSelection = () => setSelectedRowIds([]);
    const toggleAllVisible = () => {
        const allIds = rows.map((r) => r.id);
        const allSelected = allIds.every((id) => selectedRowIds.includes(id));
        if (allSelected) {
            setSelectedRowIds((prev) =>
                prev.filter((x) => !allIds.includes(x)));
        } else {
            setSelectedRowIds((prev) =>
                Array.from(new Set([...prev, ...allIds])));
        }
    };

    // Derived state for the bulk-link banner. Sum of selected invoice
    // amounts is what the user is trying to find a matching bank
    // transaction for (the wire that paid all of these). We also
    // surface the count of unique parent docs in the selection — the
    // backend works at the doc level, so that's the actual number of
    // match rows that'll be written.
    const selectedRows = useMemo(() =>
        rows.filter((r) => selectedRowIds.includes(r.id)),
        [rows, selectedRowIds],
    );
    const selectionSummary = useMemo(() => {
        let total = 0;
        let currency = null;
        const docIds = new Set();
        for (const r of selectedRows) {
            if (r.amount != null) total += Number(r.amount);
            if (r.document_id) docIds.add(r.document_id);
            if (currency == null && r.currency) currency = r.currency;
        }
        return {
            count:        selectedRows.length,
            total:        Math.round(total * 100) / 100,
            currency:     currency || 'USD',
            unique_docs:  docIds.size,
            doc_ids:      Array.from(docIds),
        };
    }, [selectedRows]);

    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput), 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    useEffect(() => { setPage(1); }, [
        statusFilter, matchFilter, merchantFilter, accountFilter,
        search, fromDate, toDate,
    ]);
    // Clear the multi-select whenever the filter set or page changes —
    // the user almost certainly didn't intend the off-screen selection
    // to still apply after they reshaped the view.
    useEffect(() => { setSelectedRowIds([]); }, [
        statusFilter, matchFilter, merchantFilter, accountFilter,
        search, fromDate, toDate, page,
    ]);

    const loadMerchants = useCallback(async () => {
        try {
            const r = await rpaMerchantList({ per_page: 200 });
            if (r?.data?.success) setMerchants(r.data.data || []);
        } catch { /* non-blocking */ }
    }, []);

    const loadAccounts = useCallback(async () => {
        try {
            const r = await rpaListStripeAccounts({ selected_only: true });
            if (r?.data?.success) setAccounts(r.data.data || []);
        } catch { /* non-blocking */ }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, per_page: perPage };
            if (statusFilter) params.status = statusFilter;
            if (matchFilter) params.match = matchFilter;
            if (merchantFilter != null) params.merchant_id = merchantFilter;
            if (accountFilter != null) params.stripe_account_id = accountFilter;
            if (search) params.search = search;
            if (fromDate) params.from_date = fromDate;
            if (toDate) params.to_date = toDate;
            const r = await rpaInvoiceList(params);
            if (r?.data?.success) {
                setRows(r.data.data || []);
                setTotal(r.data.total || 0);
            } else {
                setError(r?.data?.message || 'Failed to load invoices');
            }
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, [statusFilter, matchFilter, merchantFilter, accountFilter,
        search, fromDate, toDate, page, perPage]);

    useEffect(() => { loadMerchants(); loadAccounts(); },
              [loadMerchants, loadAccounts]);
    useEffect(() => { load(); }, [load]);

    const openFile = async (row) => {
        if (!row.artifact_id) return;
        try {
            const r = await rpaFileUrl(row.artifact_id);
            const url = r?.data?.data?.url;
            if (url) window.open(url, '_blank', 'noopener,noreferrer');
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        }
    };

    const runAutoMatch = async () => {
        setAutoMatchRunning(true);
        setAutoMatchSummary(null);
        setError(null);
        try {
            const r = await rpaReconciliationAutoMatch({
                day_window: 30, limit: 500,
            });
            if (r?.data?.success) {
                setAutoMatchSummary(r.data.data);
            } else {
                setError(r?.data?.message || 'Auto-match failed');
            }
            load();
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setAutoMatchRunning(false);
        }
    };

    // Approve / un-approve a single match row (Need-review → Reviewed).
    const approveMatch = async (row, undo = false) => {
        const matchId = row?.match?.match_id;
        if (!matchId) return;
        try {
            await rpaReconciliationReviewMatch(matchId, undo);
            load();
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        }
    };

    const unlinkMatch = async (row) => {
        const matchId = row?.match?.match_id;
        if (!matchId) return;
        if (!window.confirm('Unlink this invoice from its bank transaction?')) return;
        try {
            await rpaReconciliationUnlink(matchId);
            load();
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        }
    };

    // Open the manual-link drawer for an unmatched invoice. Fetches
    // the top-N suggested bank transactions ranked against THIS line's
    // amount + date (not the whole doc's grand total — that's why a
    // multi-row sheet's per-row "Find match" used to surface wildly
    // wrong candidates: the matcher was scoring against $1,428.07 even
    // when the user clicked on the $378.07 row).
    const openLinkDrawer = async (row) => {
        if (!row.document_id) return;
        setLinkRow(row);
        setLinkSuggestions([]);
        setLinkLoading(true);
        try {
            const params = { day_window: 30, top_n: 8 };
            if (row.line_idx != null) {
                params.line_item_idx = row.line_idx;
            }
            const r = await rpaReconciliationSuggest(row.document_id, params);
            if (r?.data?.success) {
                setLinkSuggestions(r.data.data || []);
            }
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setLinkLoading(false);
        }
    };

    const closeLinkDrawer = () => {
        setLinkRow(null);
        setLinkSuggestions([]);
    };

    const linkInvoiceTo = async (txnId) => {
        if (!linkRow?.document_id || !txnId) return;
        try {
            // Per-line match: send line_item_idx so the new match row
            // applies ONLY to this specific invoice within the doc.
            // Other line items of the same doc stay unmatched.
            const payload = {
                document_id: linkRow.document_id,
                transaction_id: txnId,
            };
            if (linkRow.line_idx != null) {
                payload.line_item_idx = linkRow.line_idx;
            }
            const r = await rpaReconciliationManualLink(payload);
            if (!r?.data?.success) throw new Error(r?.data?.message || 'Link failed');
            closeLinkDrawer();
            load();
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        }
    };

    // Open the bulk-link drawer (N-to-1 case): show unmatched bank
    // transactions ranked by how close their amount is to the SUM of
    // the selected invoices. The user is asking "which one wire paid
    // all these invoices?" — answer with the closest amount-match.
    const openBulkLinkDrawer = async () => {
        if (selectionSummary.unique_docs < 1) return;
        setBulkLinkOpen(true);
        setBulkCandidates([]);
        setBulkLoading(true);
        try {
            // Pull a healthy slice of unmatched txns scoped to the
            // current bank/merchant scope. Sort by distance from the
            // selection's total amount on the client side; bank txns
            // typically don't carry a "matches this sum" flag so the
            // proximity sort is the best we can do without a dedicated
            // server endpoint.
            const params = { per_page: 100, days: 90 };
            if (accountFilter != null) params.stripe_account_id = accountFilter;
            const r = await rpaReconciliationUnmatchedTxns(params);
            const txns = r?.data?.data || [];
            const target = Math.abs(selectionSummary.total);
            const ranked = txns
                .map((t) => {
                    const amt = Math.abs(Number(t.amount) || 0);
                    const delta = Math.abs(amt - target);
                    return { ...t, _delta: delta, _amt: amt };
                })
                .sort((a, b) => a._delta - b._delta)
                .slice(0, 20);
            setBulkCandidates(ranked);
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setBulkLoading(false);
        }
    };
    const closeBulkLinkDrawer = () => {
        setBulkLinkOpen(false);
        setBulkCandidates([]);
    };

    // Submit the bulk N-to-1 link. Each picked invoice gets its OWN
    // match row keyed on (document_id, line_item_idx) so unlinking
    // one later doesn't collapse the rest. Same txn across the whole
    // batch — that's the "N invoices → 1 wire" pattern.
    const linkBulkTo = async (txnId) => {
        if (!txnId || selectedRows.length === 0) return;
        try {
            const targets = selectedRows.map((r) => ({
                document_id:   r.document_id,
                line_item_idx: r.line_idx != null ? r.line_idx : null,
            }));
            const r = await rpaReconciliationManualLink({
                document_ids: targets,
                transaction_id: txnId,
                note: (`N-to-1: ${selectionSummary.count} invoice(s) `
                       + `across ${selectionSummary.unique_docs} doc(s) `
                       + `totalling ${selectionSummary.total} `
                       + `${selectionSummary.currency}`),
            });
            if (!r?.data?.success) throw new Error(r?.data?.message || 'Link failed');
            closeBulkLinkDrawer();
            clearSelection();
            load();
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / perPage));

    // Live KPI counts across the current page (visible rows).
    const kpis = useMemo(() => {
        const acc = {
            total,
            matched: 0,
            needs_review: 0,
            unmatched: 0,
            mismatched: 0,
        };
        for (const r of rows) {
            const mi = r.match;
            if (!mi) { acc.unmatched += 1; continue; }
            const isReviewed = mi.matched_by === 'manual' || !!mi.reviewed_at;
            if (mi.status === 'mismatch' || mi.status === 'partial') {
                if (isReviewed) acc.matched += 1; else acc.mismatched += 1;
            } else if (mi.status === 'unmatched') {
                acc.unmatched += 1;
            } else if (isReviewed) {
                acc.matched += 1;
            } else {
                acc.needs_review += 1;
            }
        }
        return acc;
    }, [rows, total]);

    return (
        <Box>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {autoMatchSummary && (
                <Alert severity="success" sx={{ mb: 2 }}
                       onClose={() => setAutoMatchSummary(null)}>
                    Auto-match complete — {autoMatchSummary.matched || 0} matched
                    ({autoMatchSummary.one_to_one || 0} one-to-one,{' '}
                    {autoMatchSummary.n_to_one || 0} N-to-1),{' '}
                    {autoMatchSummary.unmatched || 0} unmatched.
                </Alert>
            )}

            <KpiGrid>
                <KpiCard
                    label="Total invoices"
                    value={total.toLocaleString()}
                    icon={<ReceiptLongIcon fontSize="small" />}
                    accent={RPA_COLORS.robot}
                />
                <KpiCard
                    label="Matched"
                    value={kpis.matched}
                    icon={<CheckCircleIcon fontSize="small" />}
                    accent={RPA_COLORS.success}
                    hint="Visible on this page"
                />
                <KpiCard
                    label="Need review"
                    value={kpis.needs_review}
                    icon={<WarningAmberIcon fontSize="small" />}
                    accent={RPA_COLORS.warning}
                    hint="Auto-matched, awaiting approval"
                />
                <KpiCard
                    label="Unmatched"
                    value={kpis.unmatched}
                    icon={<HelpOutlineIcon fontSize="small" />}
                    accent={RPA_COLORS.danger}
                    hint="No active bank link"
                />
            </KpiGrid>

            {/* Row 1: filter dropdowns + action buttons.
                Search input lives on its own row below — gives it
                breathing space + full visible width, plus keeps the
                action cluster (Refresh / Run auto-match / Export)
                visually grouped on the right of this filter row. */}
            <Toolbar sx={{ mb: 1.25, flexWrap: 'wrap' }}>
                <Autocomplete
                    size="small"
                    sx={{ minWidth: 220 }}
                    options={[{ id: null, name: 'All merchants' }, ...merchants]}
                    value={merchants.find((m) => m.id === merchantFilter)
                           || { id: null, name: 'All merchants' }}
                    onChange={(_, v) => setMerchantFilter(v?.id ?? null)}
                    getOptionLabel={(o) => o?.name || ''}
                    isOptionEqualToValue={(a, b) => (a?.id ?? null) === (b?.id ?? null)}
                    renderInput={(p) => (
                        <TextField {...p} label="Merchant"
                            InputProps={{
                                ...p.InputProps,
                                startAdornment: <StoreIcon sx={{
                                    fontSize: 16, color: RPA_COLORS.accent, ml: 0.5,
                                }} />,
                            }}
                        />
                    )}
                />
                <Autocomplete
                    size="small"
                    sx={{ minWidth: 220 }}
                    options={[{ id: null, label: 'All banks' },
                              ...accounts.map((a) => ({
                                  id: a.id,
                                  label: `${a.institution_name || 'Bank'} — ${a.name || a.official_name || ''}${a.mask ? ' •••' + a.mask : ''}`,
                              }))]}
                    value={accounts.find((a) => a.id === accountFilter)
                           ? {
                               id: accountFilter,
                               label: (() => {
                                   const a = accounts.find((x) => x.id === accountFilter);
                                   return `${a.institution_name || 'Bank'} — ${a.name || a.official_name || ''}${a.mask ? ' •••' + a.mask : ''}`;
                               })(),
                           }
                           : { id: null, label: 'All banks' }}
                    onChange={(_, v) => setAccountFilter(v?.id ?? null)}
                    getOptionLabel={(o) => o?.label || ''}
                    isOptionEqualToValue={(a, b) => (a?.id ?? null) === (b?.id ?? null)}
                    renderInput={(p) => (
                        <TextField {...p} label="Bank account"
                            InputProps={{
                                ...p.InputProps,
                                startAdornment: <AccountBalanceIcon sx={{
                                    fontSize: 16, color: RPA_COLORS.info, ml: 0.5,
                                }} />,
                            }}
                        />
                    )}
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Match</InputLabel>
                    <Select label="Match" value={matchFilter}
                            onChange={(e) => setMatchFilter(e.target.value)}>
                        <MenuItem value=""><em>All</em></MenuItem>
                        <MenuItem value="unmatched">Unmatched</MenuItem>
                        <MenuItem value="needs_review">Need review</MenuItem>
                        <MenuItem value="matched">Matched (reviewed)</MenuItem>
                        <MenuItem value="mismatched">Mismatched</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select label="Status" value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}>
                        <MenuItem value=""><em>All</em></MenuItem>
                        <MenuItem value="EXTRACTED">Extracted</MenuItem>
                        <MenuItem value="CORRECTED">Corrected</MenuItem>
                        <MenuItem value="NEEDS_REVIEW">Needs review</MenuItem>
                        <MenuItem value="EXTRACT_FAILED">Extract failed</MenuItem>
                    </Select>
                </FormControl>
                <TextField
                    size="small" type="date" label="From"
                    InputLabelProps={{ shrink: true }}
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    sx={{ width: 140 }}
                />
                <TextField
                    size="small" type="date" label="To"
                    InputLabelProps={{ shrink: true }}
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    sx={{ width: 140 }}
                />
                <Box sx={{ flex: 1 }} />
                {/* Action cluster — Refresh and Run auto-match are
                    adjacent (both fetch fresh state), Export sits at
                    the right edge as the "produce output" action. */}
                <Tooltip title="Refresh">
                    <IconButton onClick={load} disabled={loading}
                                sx={{ border: `1px solid ${RPA_COLORS.border}` }}>
                        {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                    </IconButton>
                </Tooltip>
                <Button
                    variant="contained"
                    startIcon={autoMatchRunning
                        ? <CircularProgress size={16} color="inherit" />
                        : <AutoFixHighIcon />}
                    onClick={runAutoMatch}
                    disabled={autoMatchRunning}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                    {autoMatchRunning ? 'Matching…' : 'Run auto-match'}
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={openExportDialog}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                    Export
                </Button>
            </Toolbar>

            {/* Search row — its own line so it gets full width and
                visually separates "what data" (search) from "how to
                slice it" (filters + actions in the toolbar above). */}
            <Box sx={{ mb: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search reference / description…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{
                            fontSize: 18, color: RPA_COLORS.textMuted, mr: 0.75,
                        }} />,
                    }}
                />
            </Box>

            {/* Bulk-link action bar — only visible when the user has
                selected 2+ invoice rows (the N-to-1 use case). Shows
                the running totals + the unique parent-doc count and a
                "Link to one transaction" button that opens the
                amount-ranked candidates drawer. */}
            {selectedRows.length > 0 && (
                <Paper variant="outlined" sx={{
                    p: 1.5, mb: 1.5, borderRadius: 1.5,
                    background: 'rgba(99,102,241,0.06)',
                    borderColor: 'rgba(99,102,241,0.3)',
                }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {selectedRows.length} invoice
                            {selectedRows.length === 1 ? '' : 's'} selected
                        </Typography>
                        <Chip
                            size="small"
                            label={`${formatMoney(selectionSummary.total, selectionSummary.currency)} total`}
                            sx={{ fontWeight: 700 }}
                            color="primary"
                        />
                        {selectionSummary.unique_docs > 1 && (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`${selectionSummary.unique_docs} parent docs`}
                            />
                        )}
                        <Typography variant="caption" sx={{
                            color: RPA_COLORS.textMuted, ml: 1,
                        }}>
                            {selectionSummary.unique_docs > 1
                                ? 'Bulk link: one bank transaction → all selected invoices (N-to-1).'
                                : 'Pick more rows to link multiple invoices to one wire.'}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Button size="small" onClick={clearSelection}
                                sx={{ textTransform: 'none' }}>
                            Clear
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<LinkIcon />}
                            onClick={openBulkLinkDrawer}
                            disabled={selectionSummary.unique_docs < 1}
                            sx={{ textTransform: 'none', fontWeight: 700 }}
                        >
                            Link to one transaction
                        </Button>
                    </Stack>
                </Paper>
            )}

            <SectionPaper>
                {loading && <LinearProgress />}
                <TableShell>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    {/* Select-all toggles every row on the
                                        current page. The footer's running
                                        total + N-to-1 banner appear above
                                        the table as soon as a row is picked. */}
                                    <Checkbox
                                        size="small"
                                        indeterminate={
                                            selectedRowIds.length > 0
                                            && selectedRowIds.length < rows.length
                                        }
                                        checked={
                                            rows.length > 0
                                            && rows.every((r) => selectedRowIds.includes(r.id))
                                        }
                                        onChange={toggleAllVisible}
                                    />
                                </TableCell>
                                <TableCell>File</TableCell>
                                <TableCell>Merchant</TableCell>
                                <TableCell>Reference</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell>Invoice date</TableCell>
                                <TableCell>Due date</TableCell>
                                <TableCell>Paid date</TableCell>
                                <TableCell sx={{ minWidth: 260 }}>Match</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={11} sx={{ p: 0, border: 'none' }}>
                                        <EmptyState
                                            icon={<ReceiptLongIcon />}
                                            title="No invoices match the current filters"
                                            message="Invoices show up here as files get extracted. Use Run auto-match above to reconcile against bank transactions."
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                            {rows.map((r) => {
                                const tone = STATUS_TONE[r.status] || STATUS_TONE.extracted;
                                // Prefer the matched txn's date as paid_date
                                // when no explicit paid_date is on the row.
                                const paidDate = r.paid_date
                                    || r.match?.transaction?.date
                                    || null;
                                const isSelected = selectedRowIds.includes(r.id);
                                return (
                                    <TableRow key={r.id} hover selected={isSelected}>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                size="small"
                                                checked={isSelected}
                                                onChange={() => toggleRow(r.id)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <Tooltip title="Open file in new tab">
                                                    <IconButton size="small"
                                                                onClick={() => openFile(r)}
                                                                disabled={!r.artifact_id}>
                                                        <OpenInNewIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Typography variant="caption" sx={{
                                                    fontWeight: 600,
                                                    wordBreak: 'break-all',
                                                }}>
                                                    {r.filename || '—'}
                                                </Typography>
                                                {r.line_idx != null && (
                                                    <Chip
                                                        size="small"
                                                        label={`#${r.line_idx + 1}`}
                                                        variant="outlined"
                                                        sx={{ height: 18, fontSize: 10 }}
                                                    />
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                {r.merchant_name || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" sx={{
                                                fontFamily: 'monospace',
                                                fontWeight: 600,
                                            }}>
                                                {r.reference_no || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 240 }}>
                                            <Typography variant="caption" sx={{
                                                display: 'block',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }} title={r.description || ''}>
                                                {r.description || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="caption" sx={{
                                                fontFamily: 'monospace',
                                                fontWeight: 700,
                                                color: r.amount != null
                                                    ? RPA_COLORS.text
                                                    : RPA_COLORS.textMuted,
                                            }}>
                                                {r.amount != null
                                                    ? formatMoney(r.amount, r.currency || 'USD')
                                                    : '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption">
                                                {formatDateShort(r.invoice_date) || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption">
                                                {formatDateShort(r.due_date) || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" sx={{
                                                color: paidDate
                                                    ? RPA_COLORS.success
                                                    : RPA_COLORS.textMuted,
                                                fontWeight: paidDate ? 600 : 400,
                                            }}>
                                                {formatDateShort(paidDate) || 'Unpaid'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <MatchCell
                                                row={r}
                                                onApprove={() => approveMatch(r, false)}
                                                onUnapprove={() => approveMatch(r, true)}
                                                onUnlink={() => unlinkMatch(r)}
                                                onFindMatch={() => openLinkDrawer(r)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                color={tone.color}
                                                icon={tone.icon}
                                                label={tone.label}
                                                sx={{ fontWeight: 600 }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableShell>

                <Box sx={{
                    px: 2, py: 1,
                    borderTop: `1px solid ${RPA_COLORS.border}`,
                    background: 'rgba(0,0,0,0.02)',
                }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="caption" sx={{
                            color: RPA_COLORS.textMuted, flex: 1,
                        }}>
                            {total === 0
                                ? 'No invoices'
                                : `${((page - 1) * perPage + 1).toLocaleString()}–${Math.min(total, page * perPage).toLocaleString()} of ${total.toLocaleString()}`}
                        </Typography>
                        <FormControl size="small">
                            <Select
                                value={perPage}
                                onChange={(e) => {
                                    setPerPage(Number(e.target.value));
                                    setPage(1);
                                }}
                                sx={{
                                    fontSize: 11,
                                    '& .MuiSelect-select': { py: 0.25, pl: 0.75, pr: 2.5 },
                                }}
                            >
                                <MenuItem value={10}>10 / page</MenuItem>
                                <MenuItem value={25}>25 / page</MenuItem>
                                <MenuItem value={50}>50 / page</MenuItem>
                                <MenuItem value={100}>100 / page</MenuItem>
                            </Select>
                        </FormControl>
                        <Button size="small" onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page <= 1}>
                            Prev
                        </Button>
                        <Typography variant="caption" sx={{
                            fontWeight: 600, minWidth: 60, textAlign: 'center',
                        }}>
                            {page} / {totalPages}
                        </Typography>
                        <Button size="small" onClick={() => setPage(p => p + 1)}
                                disabled={page >= totalPages}>
                            Next
                        </Button>
                    </Stack>
                </Box>
            </SectionPaper>

            {/* Manual-link drawer — opens when the user clicks "Find
                match" on an unmatched invoice. Lists the top-N bank
                transactions ranked by the same scorer the auto-matcher
                uses; one-click apply. */}
            <Drawer
                anchor="right"
                open={!!linkRow}
                onClose={closeLinkDrawer}
                PaperProps={{ sx: { width: { xs: '100vw', md: 480 } } }}
            >
                {linkRow && (
                    <Box sx={{ p: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1}
                               sx={{ mb: 1.5 }}>
                            <LinkIcon sx={{ color: RPA_COLORS.robot }} />
                            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
                                Match invoice
                            </Typography>
                            <IconButton size="small" onClick={closeLinkDrawer}>
                                ×
                            </IconButton>
                        </Stack>
                        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                            <Typography variant="caption" sx={{ color: RPA_COLORS.textMuted }}>
                                Invoice
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {linkRow.reference_no || '—'} ·{' '}
                                {linkRow.amount != null
                                    ? formatMoney(linkRow.amount, linkRow.currency || 'USD')
                                    : '—'}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                                {linkRow.merchant_name || '—'} · {formatDateShort(linkRow.invoice_date) || '—'}
                            </Typography>
                            {linkRow.description && (
                                <Typography variant="caption" sx={{
                                    display: 'block', color: RPA_COLORS.textMuted, mt: 0.5,
                                }}>
                                    {linkRow.description}
                                </Typography>
                            )}
                        </Paper>

                        <Typography variant="caption" sx={{
                            display: 'block', color: RPA_COLORS.textMuted, mb: 0.5,
                        }}>
                            Suggested bank transactions
                        </Typography>
                        {linkLoading && <LinearProgress sx={{ mb: 1 }} />}
                        {!linkLoading && linkSuggestions.length === 0 && (
                            <Alert severity="info" sx={{ fontSize: 12 }}>
                                No candidate bank transactions within the date/amount
                                window. Try widening the day window via Run auto-match,
                                or upload the missing bank transactions.
                            </Alert>
                        )}
                        <Stack spacing={1}>
                            {linkSuggestions.map((s) => (
                                <Paper
                                    key={s.transaction_id}
                                    variant="outlined"
                                    sx={{
                                        p: 1.25,
                                        cursor: 'pointer',
                                        transition: 'border-color 120ms, background 120ms',
                                        '&:hover': {
                                            borderColor: RPA_COLORS.accent,
                                            background: 'rgba(99,102,241,0.04)',
                                        },
                                    }}
                                    onClick={() => linkInvoiceTo(s.transaction_id)}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Chip
                                            size="small"
                                            label={s.confidence != null
                                                ? `${Math.round(s.confidence * 100)}%`
                                                : '—'}
                                            color={s.confidence >= 0.95 ? 'success'
                                                : s.confidence >= 0.7 ? 'warning'
                                                : 'default'}
                                            sx={{ fontWeight: 700, minWidth: 52 }}
                                        />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {s.transaction?.merchant_name
                                                    || s.transaction?.name || '—'}
                                            </Typography>
                                            <Typography variant="caption" sx={{
                                                color: RPA_COLORS.textMuted,
                                            }}>
                                                {formatDateShort(s.transaction?.date)}
                                                {s.transaction?.reference_no
                                                    ? ` · ${s.transaction.reference_no}`
                                                    : ''}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{
                                            fontFamily: 'monospace', fontWeight: 700,
                                        }}>
                                            {formatMoney(s.transaction?.amount,
                                                         s.transaction?.currency || 'USD')}
                                        </Typography>
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>
                    </Box>
                )}
            </Drawer>

            {/* Bulk-link drawer (N-to-1). Lists unmatched bank
                transactions ranked by absolute distance from the sum
                of the selected invoice amounts — the closest amount
                match goes first. Picking one fires
                manual_link_many(document_ids[]) which writes one
                match row per parent doc, all pointing at the same
                bank transaction. */}
            <Drawer
                anchor="right"
                open={bulkLinkOpen}
                onClose={closeBulkLinkDrawer}
                PaperProps={{ sx: { width: { xs: '100vw', md: 520 } } }}
            >
                {bulkLinkOpen && (
                    <Box sx={{ p: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1}
                               sx={{ mb: 1.5 }}>
                            <LinkIcon sx={{ color: 'rgb(139,92,246)' }} />
                            <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
                                Link {selectionSummary.count} invoices to one transaction
                            </Typography>
                            <IconButton size="small" onClick={closeBulkLinkDrawer}>
                                ×
                            </IconButton>
                        </Stack>

                        {/* Selection summary — what the user is about
                            to link. Shows the picked rows + target sum
                            so they can verify before committing. */}
                        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                            <Stack direction="row" alignItems="center" spacing={1}
                                   sx={{ mb: 0.5 }}>
                                <Typography variant="caption" sx={{
                                    color: RPA_COLORS.textMuted, flex: 1,
                                }}>
                                    Selection · {selectionSummary.unique_docs} doc
                                    {selectionSummary.unique_docs === 1 ? '' : 's'}
                                </Typography>
                                <Typography variant="body2" sx={{
                                    fontFamily: 'monospace', fontWeight: 700,
                                }}>
                                    {formatMoney(selectionSummary.total,
                                                 selectionSummary.currency)}
                                </Typography>
                            </Stack>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5,
                                       maxHeight: 120, overflowY: 'auto' }}>
                                {selectedRows.slice(0, 30).map((r) => (
                                    <Chip
                                        key={r.id}
                                        size="small"
                                        label={`${r.reference_no || '—'} · ${formatMoney(r.amount, r.currency || 'USD')}`}
                                        onDelete={() => toggleRow(r.id)}
                                        sx={{ fontSize: 10.5 }}
                                    />
                                ))}
                                {selectedRows.length > 30 && (
                                    <Chip size="small" variant="outlined"
                                          label={`+${selectedRows.length - 30} more`}
                                          sx={{ fontSize: 10.5 }} />
                                )}
                            </Box>
                        </Paper>

                        <Typography variant="caption" sx={{
                            display: 'block', color: RPA_COLORS.textMuted, mb: 0.5,
                        }}>
                            Candidate bank transactions (ranked by amount proximity)
                        </Typography>
                        {bulkLoading && <LinearProgress sx={{ mb: 1 }} />}
                        {!bulkLoading && bulkCandidates.length === 0 && (
                            <Alert severity="info" sx={{ fontSize: 12 }}>
                                No unmatched bank transactions in scope. Try
                                clearing the Bank filter, or upload bank
                                transactions via Files → Upload transactions.
                            </Alert>
                        )}
                        <Stack spacing={1}>
                            {bulkCandidates.map((t) => {
                                const exact = t._delta <= 0.02;
                                const close = t._delta <= 1.0;
                                return (
                                    <Paper
                                        key={t.id}
                                        variant="outlined"
                                        sx={{
                                            p: 1.25,
                                            cursor: 'pointer',
                                            transition: 'border-color 120ms, background 120ms',
                                            borderColor: exact
                                                ? 'rgba(34,197,94,0.6)'
                                                : RPA_COLORS.border,
                                            '&:hover': {
                                                borderColor: RPA_COLORS.accent,
                                                background: 'rgba(99,102,241,0.04)',
                                            },
                                        }}
                                        onClick={() => linkBulkTo(t.id)}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Chip
                                                size="small"
                                                label={exact ? 'Exact'
                                                    : close ? '~match'
                                                    : `Δ ${formatMoney(t._delta, t.currency || 'USD')}`}
                                                color={exact ? 'success'
                                                    : close ? 'warning'
                                                    : 'default'}
                                                sx={{ fontWeight: 700, minWidth: 64 }}
                                            />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {t.merchant_name || t.name || '—'}
                                                </Typography>
                                                <Typography variant="caption" sx={{
                                                    color: RPA_COLORS.textMuted,
                                                }}>
                                                    {formatDateShort(t.date)}
                                                    {t.reference_no
                                                        ? ` · ${t.reference_no}`
                                                        : ''}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" sx={{
                                                fontFamily: 'monospace', fontWeight: 700,
                                            }}>
                                                {formatMoney(t.amount,
                                                             t.currency || 'USD')}
                                            </Typography>
                                        </Stack>
                                    </Paper>
                                );
                            })}
                        </Stack>
                    </Box>
                )}
            </Drawer>

            {/* Period Report export dialog — bank/merchant/date filters
                + format radio (XLSX / CSV / PDF). Submitting fires the
                backend report builder and streams the file back as a
                download via a Blob → anchor click. */}
            <Dialog
                open={exportOpen}
                onClose={exportRunning ? undefined : closeExportDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 1.5 } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DownloadIcon sx={{ color: RPA_COLORS.robot }} />
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            Period Report
                        </Typography>
                        <Typography variant="caption" sx={{ color: RPA_COLORS.textMuted }}>
                            Bank transactions paired with their matched invoices.
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Autocomplete
                            size="small"
                            options={[{ id: null, name: 'All merchants' }, ...merchants]}
                            value={merchants.find((m) => m.id === exportMerchant)
                                   || { id: null, name: 'All merchants' }}
                            onChange={(_, v) => setExportMerchant(v?.id ?? null)}
                            getOptionLabel={(o) => o?.name || ''}
                            isOptionEqualToValue={(a, b) =>
                                (a?.id ?? null) === (b?.id ?? null)}
                            renderInput={(p) => (
                                <TextField {...p} label="Merchant" />
                            )}
                        />
                        <Autocomplete
                            size="small"
                            options={[{ id: null, label: 'All banks' },
                                      ...accounts.map((a) => ({
                                          id: a.id,
                                          label: `${a.institution_name || 'Bank'} — ${a.name || a.official_name || ''}${a.mask ? ' •••' + a.mask : ''}`,
                                      }))]}
                            value={accounts.find((a) => a.id === exportBank)
                                   ? {
                                       id: exportBank,
                                       label: (() => {
                                           const a = accounts.find((x) => x.id === exportBank);
                                           return `${a.institution_name || 'Bank'} — ${a.name || a.official_name || ''}${a.mask ? ' •••' + a.mask : ''}`;
                                       })(),
                                   }
                                   : { id: null, label: 'All banks' }}
                            onChange={(_, v) => setExportBank(v?.id ?? null)}
                            getOptionLabel={(o) => o?.label || ''}
                            isOptionEqualToValue={(a, b) =>
                                (a?.id ?? null) === (b?.id ?? null)}
                            renderInput={(p) => (
                                <TextField {...p} label="Bank account" />
                            )}
                        />
                        <Stack direction="row" spacing={1.5}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="From"
                                InputLabelProps={{ shrink: true }}
                                value={exportFromDate}
                                onChange={(e) => setExportFromDate(e.target.value)}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="To"
                                InputLabelProps={{ shrink: true }}
                                value={exportToDate}
                                onChange={(e) => setExportToDate(e.target.value)}
                            />
                        </Stack>

                        <Divider />

                        <Box>
                            <Typography variant="caption" sx={{
                                fontWeight: 700, color: RPA_COLORS.textMuted,
                                textTransform: 'uppercase', letterSpacing: 0.5,
                            }}>
                                Format
                            </Typography>
                            <RadioGroup
                                row
                                value={exportFormat}
                                onChange={(e) => setExportFormat(e.target.value)}
                                sx={{ mt: 0.5 }}
                            >
                                <FormControlLabel
                                    value="xlsx"
                                    control={<Radio size="small" />}
                                    label={
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <GridOnIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                                            <span>Excel (.xlsx)</span>
                                        </Stack>
                                    }
                                />
                                <FormControlLabel
                                    value="csv"
                                    control={<Radio size="small" />}
                                    label={
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <TableChartIcon sx={{ fontSize: 16, color: '#475569' }} />
                                            <span>CSV</span>
                                        </Stack>
                                    }
                                />
                                <FormControlLabel
                                    value="pdf"
                                    control={<Radio size="small" />}
                                    label={
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <PictureAsPdfIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                                            <span>PDF</span>
                                        </Stack>
                                    }
                                />
                            </RadioGroup>
                        </Box>

                        <Alert severity="info" icon={<DownloadIcon fontSize="small" />}
                               sx={{ fontSize: 12 }}>
                            One row per bank transaction. Matched invoices contribute
                            their <b>reference</b>, <b>description</b>, <b>date</b>,
                            and <b>amount</b>. A <b>Match Type</b> column flags each
                            row as Auto, Manual, or LLM-adjudicated; manual matches
                            also show the name of the user who linked them.
                        </Alert>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeExportDialog} disabled={exportRunning}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={exportRunning
                            ? <CircularProgress size={14} color="inherit" />
                            : <DownloadIcon />}
                        onClick={runExport}
                        disabled={exportRunning}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                        {exportRunning ? 'Generating…' : 'Export Period Report'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}


/**
 * MatchCell — renders the match state for one invoice row.
 *
 * States:
 *   1. No match (`r.match == null`) → "Unmatched" chip + "Find match"
 *      button that opens the suggestions drawer.
 *   2. Auto-matched, awaiting review → status chip + matched-txn summary
 *      + Approve (thumbs-up) + Unlink buttons.
 *   3. Matched (manual OR reviewed auto) → green status chip + matched-txn
 *      summary + Unlink button.
 *   4. Mismatch / partial → orange chip + matched-txn summary + Approve /
 *      Unlink (treat as "needs human attention").
 */
function MatchCell({ row, onApprove, onUnapprove, onUnlink, onFindMatch }) {
    const mi = row.match;

    // (1) — unmatched.
    if (!mi) {
        return (
            <Stack direction="row" spacing={0.5} alignItems="center">
                <Chip
                    size="small"
                    label="Unmatched"
                    variant="outlined"
                    sx={{
                        color: RPA_COLORS.textMuted,
                        borderColor: RPA_COLORS.border,
                        fontWeight: 600,
                    }}
                />
                <Button
                    size="small"
                    startIcon={<LinkIcon fontSize="small" />}
                    onClick={onFindMatch}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                    Find match
                </Button>
            </Stack>
        );
    }

    const isReviewed = mi.matched_by === 'manual' || !!mi.reviewed_at;
    const isMismatch = mi.status === 'mismatch' || mi.status === 'partial';
    const needsReview = !isReviewed && !isMismatch
                        && mi.status !== 'unmatched';

    const tone = isMismatch ? 'warning' : (isReviewed ? 'success' : 'info');
    const label = isMismatch ? 'Mismatch'
                : isReviewed ? 'Matched'
                : needsReview ? 'Need review'
                : (mi.status || '—');

    const txn = mi.transaction;

    const bundleSize = mi.bundle_size || 1;

    return (
        <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title={mi.notes || ''} arrow>
                <Chip
                    size="small"
                    color={tone}
                    label={
                        <span>
                            {label}
                            {mi.confidence != null && (
                                <span style={{ opacity: 0.7, marginLeft: 4 }}>
                                    {Math.round(mi.confidence * 100)}%
                                </span>
                            )}
                        </span>
                    }
                    sx={{ fontWeight: 600 }}
                />
            </Tooltip>
            {/* N-to-1 indicator — appears when this match is part of
                a bundle (multiple invoices share the same bank txn).
                Hover for the count; the matched txn tooltip below
                shows the bank-side details. */}
            {bundleSize > 1 && (
                <Tooltip
                    title={`Part of a ${bundleSize}-invoice bundle linked to this transaction.`}
                    arrow
                >
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`Bundle ×${bundleSize}`}
                        sx={{
                            fontWeight: 700,
                            height: 22,
                            fontSize: 10.5,
                            borderColor: 'rgba(139,92,246,0.5)',
                            color: 'rgb(139,92,246)',
                            cursor: 'help',
                        }}
                    />
                </Tooltip>
            )}
            {txn && (
                <Tooltip
                    title={
                        <Box>
                            <div><b>Bank txn</b></div>
                            <div>{txn.merchant_name || txn.name || '—'}</div>
                            <div>{formatDateShort(txn.date)}{' · '}
                                {formatMoney(txn.amount, txn.currency || 'USD')}</div>
                            {txn.reference_no && <div>ref: {txn.reference_no}</div>}
                            {txn.description && (
                                <div style={{ marginTop: 4, fontSize: 11 }}>
                                    {String(txn.description).slice(0, 120)}
                                </div>
                            )}
                        </Box>
                    }
                    arrow placement="left"
                >
                    <Typography variant="caption" sx={{
                        fontFamily: 'monospace',
                        cursor: 'help',
                        color: RPA_COLORS.textMuted,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 80,
                    }}>
                        {formatMoney(txn.amount, txn.currency || 'USD')}
                    </Typography>
                </Tooltip>
            )}
            {(needsReview || isMismatch) && (
                <Tooltip title="Approve match">
                    <IconButton size="small" onClick={onApprove}
                                sx={{ color: RPA_COLORS.success }}>
                        <ThumbUpAltIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
            {isReviewed && mi.matched_by !== 'manual' && (
                <Tooltip title="Un-approve — moves back to Need review">
                    <IconButton size="small" onClick={onUnapprove}
                                sx={{ color: RPA_COLORS.warning }}>
                        <ThumbUpAltIcon fontSize="small"
                                        sx={{ transform: 'rotate(180deg)' }} />
                    </IconButton>
                </Tooltip>
            )}
            <Tooltip title="Unlink from bank transaction">
                <IconButton size="small" onClick={onUnlink}
                            sx={{ color: RPA_COLORS.danger }}>
                    <LinkOffIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Stack>
    );
}
